import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button, buttonVariants } from './ui/button';
import { Bell, CaretDown, Plus } from '@phosphor-icons/react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { nameInitial } from '../utils/userDisplay';
import { CurrencySelector } from './CurrencySelector';
import { UserMenuPanel } from './UserMenuPanel';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useElementHeight } from '../hooks/use-element-height';
import { EmailVerificationBanner } from './EmailVerificationBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const { user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navLinks, setNavLinks] = useState([]);
  const menuRef = useRef(null);
  const [headerRef, headerHeight] = useElementHeight();
  const logoUrl = branding?.logo_path
    ? (branding.logo_path.startsWith('http') ? branding.logo_path : `${API}/files/${branding.logo_path}`)
    : null;
  const platformName = branding?.platform_name || 'ATÔ';
  // Tagline: admin can override per-locale via branding.taglines[lang].
  // When empty/missing, falls back to the i18n translation. Uses
  // i18n.language so the language picker still drives the locale.
  const lang = (i18n.language || 'fr').split('-')[0];
  const overrideTagline = (branding?.taglines?.[lang] || '').trim();
  const platformTagline = overrideTagline || t('home.tagline');

  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => {
      axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
        .then(res => setUnreadCount(res.data.unread_count))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/nav`).then(res => setNavLinks(res.data || [])).catch(() => setNavLinks([]));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <header ref={headerRef} className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-border/30 z-40" data-testid="header">
        <EmailVerificationBanner />
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo">
          {logoUrl ? (
            <img src={logoUrl} alt={platformName} className="h-8 w-auto object-contain" />
          ) : (
            <div className="flex flex-col md:flex-row md:items-baseline md:gap-2 min-w-0">
              <span className="text-xl font-black font-heading text-text_strong leading-none">{platformName}</span>
              <span
                className="hidden lg:block text-[10px] md:text-xs text-text_muted leading-tight mt-0.5 md:mt-0 md:whitespace-nowrap md:truncate md:max-w-[220px] lg:max-w-none"
                title={platformTagline}
                data-testid="header-tagline"
              >
                {platformTagline}
              </span>
            </div>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-2.5 ml-8" data-testid="header-nav">
          <Link
            to="/create"
            data-testid="nav-create"
            className={buttonVariants({ variant: 'primary', size: 'sm' })}
          >
            <Plus size={14} weight="bold" />
            {t('nav.postAd')}
          </Link>

          {/* Iter 84 — dynamic Layout > Header links. In ADDITION to the built-in
              actions above. Hidden on tablet (<lg) to keep the top bar compact;
              future work: expose them in the mobile drawer too. */}
          {navLinks.length > 0 && (
            <div className="hidden lg:flex items-center gap-2 ml-2 pl-2 border-l border-border/60" data-testid="header-custom-nav">
              {navLinks.map(link => {
                const label = link.title_i18n?.[lang] || link.title_i18n?.fr || link.title_i18n?.en || '';
                if (!label) return null;
                const testId = `nav-custom-${link.link_id}`;
                const cls = "text-[13px] font-medium text-text_muted hover:text-primary transition-colors px-1.5";
                if (link.external || (link.url || '').startsWith('http')) {
                  return (
                    <a key={link.link_id} href={link.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>
                      {label}
                    </a>
                  );
                }
                return (
                  <Link key={link.link_id} to={link.url} className={cls} data-testid={testId}>
                    {label}
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        <div className="flex items-center gap-1.5">
          <LanguageSwitcher />
          <CurrencySelector />
          
          {user && (
            <button
              onClick={() => navigate('/notifications')}
              className="relative p-1.5 rounded-full hover:bg-surface_muted transition-colors"
              data-testid="notification-bell"
            >
              <Bell size={20} weight={unreadCount > 0 ? 'fill' : 'regular'} className={unreadCount > 0 ? 'text-primary' : 'text-text_muted'} />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 min-w-[16px] h-[16px] bg-danger text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5" data-testid="notification-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          )}
          
          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-1 text-sm font-medium text-text_main hover:text-primary transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                  {user.picture ? (
                    <img src={user.picture.startsWith('http') ? user.picture : `${API}/files/${user.picture}`} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    nameInitial(user)
                  )}
                </div>
                <CaretDown size={12} className={`transition-transform hidden md:block text-text_muted ${menuOpen ? 'rotate-180' : ''}`} />
              </button>
              {menuOpen && (
                <UserMenuPanel onClose={() => setMenuOpen(false)} variant="dropdown" />
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" className={buttonVariants({ variant: 'secondary', size: 'sm' })} data-testid="login-button">
                {t('nav.login')}
              </Link>
              <Link to="/register" className={buttonVariants({ variant: 'accent', size: 'sm' })} data-testid="register-button">
                {t('nav.register')}
              </Link>
            </div>
          )}
        </div>
      </div>
      </header>
      {/* Spacer reserving the fixed header's height so page content never
          loads hidden underneath it (height tracks the header live). */}
      <div style={{ height: headerHeight }} aria-hidden="true" data-testid="header-spacer" />
    </>
  );
};
